import { ApiErrorCode, createApiError } from '@/modules/common/api/errors';
import {
  mapIntegrationProviderToDTO,
  mapSelectedIntegrationFromDTO,
} from '@/modules/common/mappers/integration.mapper';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityIntegration } from '@/modules/common/types/entity-integration';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getIntegrationIcon } from '@/modules/common/utils/integration-icons';

import { ClientIntegration } from '../types/client';
import {
  CreateIntegrationInfoRequest,
  PatchClientIntegrationsRequest,
} from '../types/dto/request/client-integrations.dto';

/**
 * Converts generic EntityIntegration to ClientIntegration.
 * Maps `entities` to `clients` and `selectedEntity` to `selectedValue`.
 * Sets allClientsLinked=true when the integration has no available clients and no
 * selected value, meaning all external clients are already linked to other clients.
 */
export const mapEntityIntegrationToClientIntegration = (
  entity: EntityIntegration
): ClientIntegration => {
  const clients = entity.entities;
  const selectedValue = entity.selectedEntity ?? null;
  const allClientsLinked = Array.isArray(clients) && clients.length === 0 && !selectedValue;

  return {
    id: entity.id,
    type: entity.type,
    icon: entity.icon,
    clients,
    selectedValue,
    allClientsLinked,
  };
};

/**
 * Converts array of EntityIntegration to ClientIntegration array.
 */
export const mapEntityIntegrationsToClientIntegrations = (
  entities: EntityIntegration[]
): ClientIntegration[] => {
  return entities.map(mapEntityIntegrationToClientIntegration);
};

/**
 * Maps ClientIntegration array to CreateIntegrationInfoRequest array.
 * Only includes integrations with selected clients.
 */
export const mapClientIntegrationsToDTO = (
  integrations?: ClientIntegration[]
): CreateIntegrationInfoRequest[] | undefined => {
  if (!integrations || integrations.length === 0) return undefined;

  return integrations
    .filter(integration => integration.selectedValue)
    .filter(integration => Number.isFinite(Number(integration.selectedValue?.id)))
    .map(integration => {
      const serviceType = integration.type as IntegrationServices;

      return {
        integrationProviderType: mapIntegrationProviderToDTO(serviceType),
        connectToLinkId: Number(integration.selectedValue?.id),
      };
    });
};

/**
 * Maps ClientIntegration array to PatchClientIntegrationsRequest.
 * Used for PATCH /api/clients/integrations endpoint.
 */
export const mapClientIntegrationsToPatchRequest = (
  clientId: string | number,
  integrations?: ClientIntegration[]
): PatchClientIntegrationsRequest => {
  const id = typeof clientId === 'number' ? clientId : Number(clientId);

  if (!Number.isFinite(id)) {
    const originalError = new Error(
      `Invalid clientId provided to mapClientIntegrationsToPatchRequest: ${clientId}`
    );
    throw createApiError(
      ApiErrorCode.CLIENT_UPDATE_FAILED,
      originalError,
      'Invalid client identifier for integrations update'
    );
  }

  const integrationsDTO = mapClientIntegrationsToDTO(integrations);

  return {
    entityId: id,
    integrations: {
      integrations: integrationsDTO || [],
    },
  };
};

/**
 * Maps integration info responses from API to client integrations.
 * Converts DTO to domain model.
 */
export const mapIntegrationsFromResponse = (
  rawIntegrations: IntegrationInfoResponse[]
): ClientIntegration[] => {
  return rawIntegrations.map(integration => {
    const { type, selectedEntity } = mapSelectedIntegrationFromDTO(integration);

    const entityIntegration: EntityIntegration = {
      id: type,
      type,
      icon: getIntegrationIcon(type as IntegrationServices),
      entities: null,
      selectedEntity,
    };

    return mapEntityIntegrationToClientIntegration(entityIntegration);
  });
};
